import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import collect from 'collect.js';
import classnames from 'classnames';
import useDeals from '../../../hooks/deals/useDeals';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import usePromoCode from '../../../hooks/order/usePromoCode';
import useAuth from '../../../hooks/global/useAuth';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import useModalRouter from '../../../modules/mobile/common/hooks/useModalRouter';
import useOffersTranslations from '../../../features/offers/hooks/useOffersTranslations';
import useGoogleReview from '../../../hooks/google-review/useGoogleReview';
import useMobileAppSignupBonus from '../useMobileAppSignupBonus';
import useWelcomeBonus from '../useWelcomeBonus';
import {
  DEAL_TYPES,
  OfferTypeEnum,
  PROMO_CODE_DISCOUNT_TYPES,
  PROMO_CODE_TYPES,
} from '../../../services/exports/Constants';
import { Modals } from '../../../modules/mobile/exports/Enums';
import { PromoCodeData } from '../../../store/api-slices';
import { CouponDetailsInterface, Offer } from '../../../services/exports/Interfaces';
import useAnalytics from '../../../hooks/analytics/useAnalytics';
import { useSelector } from 'react-redux';
import { selectCompanyId } from '../../../store/slices/InitialDataSlice';
import { selectAppliedOffers } from '../../../store/slices/OfferSlice';
import useDiscountCardTag, { DiscountCardTag } from '../../../features/discount-exclusivity/useDiscountCardTag';
import {
  DealEventContext,
  DealEventProps,
  OfferEventType,
  bonusEventProps,
  masterPromoEventProps,
  offerEventProps,
  smartPricingEventProps,
} from '../../../utils/analytics/dealEventProps';

const EXPECTED_OFFERS_ORDER_BY_TYPE: OfferTypeEnum[] = [
  OfferTypeEnum.Bogo,
  OfferTypeEnum.FreeItem,
  OfferTypeEnum.PercentageOff,
  OfferTypeEnum.AmountOff,
] as const;

const getOfferBadge = (offer: Offer): string => {
  if (offer.type === OfferTypeEnum.FreeItem) return 'FREE ITEM';

  return 'DEAL';
};

export interface DealItem {
  key: string | number;
  title: string;
  description: string;
  footer?: string;
  onClick?: () => void;
  image?: string;
  isPlaceholder?: boolean;
  badge?: string;
  className?: string;
  tag?: DiscountCardTag;
  onRemove?: () => void;
  analytics?: DealEventProps;
}

interface UseDealsItemsReturn {
  items: DealItem[];
  hasImages: boolean;
}

export default function useDealsItems(dealClassName?: string): UseDealsItemsReturn {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:DealsCarousel' });
  const { activeOffers, promoCodes, smartPricingDeals, showMasterPromoCode, hasImages, franchise, company } =
    useDeals();
  // Placeholder used for deals without their own image so every card renders as an
  // ImageCard and stays aligned. Franchise image only exists for franchise companies,
  // so fall back to the company's own picture/logo for standalone restaurants.
  const fallbackImage = company?.pictures?.[0] ?? company?.logo_url ?? franchise?.image_url;
  const { formatCurrency, toggleLoginModal, toggleOfferModal } = useAppMethods();
  const { openModal } = useModalRouter();
  const { isLoggedIn } = useAuth();
  const { appliedPromoCode, showLoginPrompt, removePromoCode, applyPromoCode, openMultiPromoModal } = usePromoCode();
  const { hasGooglePromotion, hasUploadedGoogleReview, googlePromotion } = useGoogleReview({ autoLoad: true });
  const { isMobileApp } = useMobileApp();
  const { getLabelByOffer } = useOffersTranslations();
  const mobileAppSignupBonusOffer = useMobileAppSignupBonus(dealClassName);
  const welcomeBonusOffer = useWelcomeBonus(dealClassName);
  const { trackOfferClicked } = useAnalytics();
  const companyId = useSelector(selectCompanyId);
  const appliedOffers = useSelector(selectAppliedOffers);
  const getCardTag = useDiscountCardTag();
  const analyticsCtx: DealEventContext = { restaurantId: companyId, isLoggedIn };

  const withClickTracking =
    (analytics: DealEventProps, original?: () => void): (() => void) =>
    () => {
      trackOfferClicked(analytics);
      original?.();
    };

  const getDiscount = (coupon: PromoCodeData): string => {
    const formatter = (discount: number) =>
      ({
        [PROMO_CODE_DISCOUNT_TYPES.PERCENTAGE]: `${discount}%`,
        [PROMO_CODE_DISCOUNT_TYPES.FIXED_AMOUNT]: formatCurrency(discount),
      })[coupon?.discount_type] ?? formatCurrency(discount);

    const firstComponent = collect(coupon?.components).first();
    if (firstComponent && collect(coupon.components).every((item) => item.discount === firstComponent.discount)) {
      return `${coupon.components.length} x ${formatter(firstComponent.discount)}`;
    }

    return collect(coupon?.components)
      .map(({ discount }) => formatter(discount))
      .implode(', ');
  };

  const getPromoCodeDescription = (promoCode: PromoCodeData): string => {
    if (promoCode.type === PROMO_CODE_TYPES.MULTI_PROMO_CODE) {
      return t(`discounts.description.${PROMO_CODE_TYPES.MULTI_PROMO_CODE}`, {
        discount: getDiscount(promoCode),
        count: promoCode.components.length,
        code: promoCode.code,
      });
    }

    return t(`discounts.description.${PROMO_CODE_TYPES.ONE_TIME_PROMO_CODE}`, {
      discount: getDiscount(promoCode),
      code: promoCode.code,
    });
  };

  const onLogin = (): void => {
    if (isLoggedIn) return;
    if (isMobileApp) {
      return openModal({ id: Modals.Login });
    }

    toggleLoginModal({
      openModal: true,
      onCloseRedirect: () => null,
    });
  };

  const _applyPromoCode = (code: string): void => {
    if (!isLoggedIn) {
      return onLogin();
    }

    applyPromoCode(code);
  };

  const promoCodeDeals: DealItem[] = collect(promoCodes)
    .map((promoCode): DealItem => {
      const component = collect(promoCode.components).firstWhere('is_used', false);
      const title =
        t('discounts.title', { discount: getDiscount(promoCode) }) +
        (component?.usage_threshold
          ? ' ' + t('discounts.usage_threshold', { amount: formatCurrency(component.usage_threshold) })
          : '');

      const analytics = masterPromoEventProps(promoCode, analyticsCtx);

      return {
        key: promoCode.code,
        title,
        description: getPromoCodeDescription(promoCode),
        footer: promoCode.code === appliedPromoCode ? t('deals.buttons.remove') : t('deals.buttons.add'),
        onClick: withClickTracking(analytics, () => {
          // Multi-promo cards open the detail modal in preview (Apply must be clicked there);
          // single-use codes still toggle apply/remove directly on the card.
          if (promoCode.type === PROMO_CODE_TYPES.MULTI_PROMO_CODE) {
            return openMultiPromoModal(promoCode as unknown as CouponDetailsInterface);
          }

          return promoCode.code === appliedPromoCode ? removePromoCode() : _applyPromoCode(promoCode.code);
        }),
        className: classnames(dealClassName, 'cursor-pointer'),
        image: promoCode.image_url ?? fallbackImage,
        isPlaceholder: !promoCode.image_url,
        badge: 'PROMO',
        // Applied promo cards get an inline Remove link (prototype); it clears the code directly.
        onRemove: promoCode.code === appliedPromoCode ? () => removePromoCode() : undefined,
        tag: getCardTag({
          kind: 'promo',
          isApplied: promoCode.code === appliedPromoCode,
          savings: getDiscount(promoCode),
        }),
        analytics,
      };
    })
    .toArray();

  const smartPricing: DealItem[] = collect(smartPricingDeals)
    .map(
      (item): DealItem => ({
        key: item.id,
        title: item.name?.trim() || t(`deals.${DEAL_TYPES.SMART_PRICING_RULE}.title`),
        description: t(`deals.${DEAL_TYPES.SMART_PRICING_RULE}.description`),
        className: dealClassName,
        image: item.image_url ?? fallbackImage,
        isPlaceholder: !item.image_url,
        badge: 'DEAL',
        analytics: smartPricingEventProps(item, analyticsCtx),
      }),
    )
    .toArray();

  const offers: DealItem[] = collect(activeOffers)
    .sortBy((offer) => EXPECTED_OFFERS_ORDER_BY_TYPE.indexOf(offer.type))
    .map((offer): DealItem => {
      const analytics = offerEventProps(offer, analyticsCtx);

      return {
        key: offer.id,
        title: getLabelByOffer(offer),
        description: '',
        className: classnames(dealClassName, 'cursor-pointer'),
        onClick: withClickTracking(analytics, () => {
          if (!isMobileApp) {
            return toggleOfferModal({ openModal: true, offer });
          }

          openModal({
            id: Modals.OfferModal,
            props: { offer },
          });
        }),
        footer: t('deals.buttons.show_details'),
        image: offer.image_url ?? fallbackImage,
        isPlaceholder: !offer.image_url,
        badge: getOfferBadge(offer),
        // Offers stack with each other, so an offer card is only ever "available" or "replaces"
        // (blocked by an active promo/gift/points) or "applied" — never blocked by another offer.
        tag: getCardTag({
          kind: 'offer',
          isApplied: (appliedOffers ?? []).some((applied) => applied.id === offer.id),
        }),
        analytics,
      };
    })
    .toArray();

  const items = useMemo<DealItem[]>(() => {
    const pushBonus = (
      c: any,
      offer: { render: () => DealItem },
      type: OfferEventType.WelcomeBonus | OfferEventType.MobileSignup,
    ) => {
      const rendered = offer.render();
      const analytics = bonusEventProps(type, rendered.image, analyticsCtx);

      return c.push({
        ...rendered,
        image: rendered.image ?? fallbackImage,
        isPlaceholder: !rendered.image,
        analytics,
        onClick: withClickTracking(analytics, rendered.onClick),
      });
    };

    return collect<DealItem>()
      .when(showMasterPromoCode, (c) => c.merge(promoCodeDeals))
      .merge(smartPricing)
      .merge(offers)
      .when(!!welcomeBonusOffer, (c) => pushBonus(c, welcomeBonusOffer, OfferEventType.WelcomeBonus))
      .when(!!mobileAppSignupBonusOffer, (c) => pushBonus(c, mobileAppSignupBonusOffer, OfferEventType.MobileSignup))
      .toArray();
  }, [
    showMasterPromoCode,
    smartPricingDeals,
    promoCodes,
    hasGooglePromotion,
    hasUploadedGoogleReview,
    googlePromotion,
    appliedPromoCode,
    appliedOffers,
    showLoginPrompt,
    isLoggedIn,
    offers,
    fallbackImage,
    welcomeBonusOffer,
    mobileAppSignupBonusOffer,
  ]);

  return { items, hasImages };
}
